import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  User, Save, LogOut, Eye, ExternalLink, Loader2, Upload, X,
  Plus, Trash2, Briefcase, Image as ImageIcon, MapPin, Phone, Globe, Linkedin, Instagram
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FreelancerDashboard() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [portfolioForm, setPortfolioForm] = useState({ title: "", description: "", client: "", year: "", category: "", images: [] });
  const navigate = useNavigate();

  const token = localStorage.getItem("freelancer_token");

  useEffect(() => {
    if (!token) {
      navigate("/freelancer-giris");
      return;
    }
    loadProfile();
  }, [token, navigate]);

  const loadProfile = async () => {
    try {
      const res = await axios.get(`${API}/freelancer/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("freelancer_token");
        navigate("/freelancer-giris");
      }
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/freelancer/me`, profile, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Profil kaydedildi!");
    } catch (err) {
      toast.error("Kaydetme hatası");
    } finally {
      setSaving(false);
    }
  };

  const compressImage = (file, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let w = img.width, h = img.height;
          if (w > maxWidth) { h = (maxWidth / w) * h; w = maxWidth; }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file, field === "cover_image" ? 1200 : 400, 0.8);
    setProfile({ ...profile, [field]: compressed });
  };

  const handlePortfolioImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 6 - portfolioForm.images.length;
    if (remaining <= 0) return;
    for (const file of files.slice(0, remaining)) {
      const compressed = await compressImage(file, 800, 0.7);
      setPortfolioForm((prev) => ({ ...prev, images: [...prev.images, compressed] }));
    }
    e.target.value = "";
  };

  const addPortfolioItem = async () => {
    if (!portfolioForm.title) {
      toast.error("Proje adı gerekli");
      return;
    }
    try {
      await axios.post(`${API}/freelancer/portfolio`, portfolioForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Proje eklendi!");
      setPortfolioForm({ title: "", description: "", client: "", year: "", category: "", images: [] });
      loadProfile();
    } catch {
      toast.error("Hata oluştu");
    }
  };

  const deletePortfolioItem = async (itemId) => {
    if (!window.confirm("Bu projeyi silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/freelancer/portfolio/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Proje silindi");
      loadProfile();
    } catch {
      toast.error("Silme hatası");
    }
  };

  const logout = () => {
    localStorage.removeItem("freelancer_token");
    localStorage.removeItem("freelancer_slug");
    navigate("/");
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-stone-300" /></div>;
  if (!profile) return null;

  const TABS = [
    { id: "profile", label: "Profil", icon: User },
    { id: "portfolio", label: "Portfolyo", icon: Briefcase },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 font-serif">Freelancer Paneli</h1>
            <p className="text-sm text-stone-500">{profile.full_name}</p>
          </div>
          <div className="flex gap-2">
            {profile.is_published && profile.slug && (
              <Link to={`/freelancer/${profile.slug}`} target="_blank">
                <Button variant="outline" size="sm" className="gap-1.5">
                  <ExternalLink className="w-4 h-4" /> Profili Gör
                </Button>
              </Link>
            )}
            <Button onClick={logout} variant="ghost" size="sm" className="gap-1.5 text-stone-500">
              <LogOut className="w-4 h-4" /> Çıkış
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-lg p-1 border border-stone-200 w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id ? "bg-[#1B4F72] text-white" : "text-stone-600 hover:bg-stone-50"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-6">
            {/* Cover & Profile Image */}
            <div className="relative">
              <div className="h-32 bg-gradient-to-r from-[#1B4F72] to-[#2E86AB] rounded-lg overflow-hidden">
                {profile.cover_image && <img src={profile.cover_image} alt="" className="w-full h-full object-cover" />}
                <label className="absolute bottom-2 right-2 cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, "cover_image")} />
                  <span className="bg-white/90 text-stone-700 text-xs px-2 py-1 rounded flex items-center gap-1">
                    <Upload className="w-3 h-3" /> Kapak
                  </span>
                </label>
              </div>
              <div className="absolute -bottom-8 left-6">
                <div className="w-20 h-20 rounded-full border-4 border-white bg-stone-100 overflow-hidden">
                  {profile.profile_image ? (
                    <img src={profile.profile_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-400">
                      <User className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <label className="absolute bottom-0 right-0 cursor-pointer bg-[#1B4F72] text-white p-1 rounded-full">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, "profile_image")} />
                  <Upload className="w-3 h-3" />
                </label>
              </div>
            </div>

            <div className="pt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-stone-500">Ad Soyad</Label>
                <Input value={profile.full_name || ""} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-stone-500">Ünvan</Label>
                <Input value={profile.title || ""} onChange={(e) => setProfile({ ...profile, title: e.target.value })} placeholder="3D Designer, Stand Tasarımcısı..." className="mt-1" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs text-stone-500">Hakkında</Label>
                <Textarea value={profile.bio || ""} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} rows={4} placeholder="Kendinizi tanıtın..." className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-stone-500">Yetenekler (virgülle ayırın)</Label>
                <Input value={(profile.skills || []).join(", ")} onChange={(e) => setProfile({ ...profile, skills: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="3D Modelleme, AutoCAD, Render..." className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-stone-500">Deneyim (yıl)</Label>
                <Input type="number" value={profile.experience_years || ""} onChange={(e) => setProfile({ ...profile, experience_years: parseInt(e.target.value) || 0 })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-stone-500">Saatlik Ücret</Label>
                <Input value={profile.hourly_rate || ""} onChange={(e) => setProfile({ ...profile, hourly_rate: e.target.value })} placeholder="500 TL/saat" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-stone-500">Şehir</Label>
                <Input value={profile.city || ""} onChange={(e) => setProfile({ ...profile, city: e.target.value })} placeholder="İstanbul" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-stone-500">Telefon</Label>
                <Input value={profile.phone || ""} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+90 5XX XXX XX XX" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-stone-500">Website</Label>
                <Input value={profile.website || ""} onChange={(e) => setProfile({ ...profile, website: e.target.value })} placeholder="https://..." className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-stone-500">LinkedIn</Label>
                <Input value={profile.linkedin || ""} onChange={(e) => setProfile({ ...profile, linkedin: e.target.value })} placeholder="linkedin.com/in/..." className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-stone-500">Instagram</Label>
                <Input value={profile.instagram || ""} onChange={(e) => setProfile({ ...profile, instagram: e.target.value })} placeholder="@kullanici" className="mt-1" />
              </div>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={profile.is_published || false}
                  onChange={(e) => setProfile({ ...profile, is_published: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm text-stone-700">Profili Yayınla</span>
              </label>
              <Button onClick={saveProfile} disabled={saving} className="bg-[#1B4F72] hover:bg-[#154360] gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Kaydet
              </Button>
            </div>
          </div>
        )}

        {/* Portfolio Tab */}
        {activeTab === "portfolio" && (
          <div className="space-y-6">
            {/* Add Portfolio Item */}
            <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-4">
              <h3 className="font-semibold text-stone-900">Yeni Proje Ekle</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-stone-500">Proje Adı *</Label>
                  <Input value={portfolioForm.title} onChange={(e) => setPortfolioForm({ ...portfolioForm, title: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Müşteri</Label>
                  <Input value={portfolioForm.client} onChange={(e) => setPortfolioForm({ ...portfolioForm, client: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Yıl</Label>
                  <Input value={portfolioForm.year} onChange={(e) => setPortfolioForm({ ...portfolioForm, year: e.target.value })} placeholder="2024" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Kategori</Label>
                  <Input value={portfolioForm.category} onChange={(e) => setPortfolioForm({ ...portfolioForm, category: e.target.value })} placeholder="3D Render, Stand Tasarımı..." className="mt-1" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-stone-500">Açıklama</Label>
                  <Textarea value={portfolioForm.description} onChange={(e) => setPortfolioForm({ ...portfolioForm, description: e.target.value })} rows={2} className="mt-1" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-stone-500">Görseller (max 6)</Label>
                  <div className="grid grid-cols-6 gap-2 mt-2">
                    {portfolioForm.images.map((img, i) => (
                      <div key={i} className="aspect-square relative border rounded overflow-hidden">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setPortfolioForm({ ...portfolioForm, images: portfolioForm.images.filter((_, j) => j !== i) })} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {portfolioForm.images.length < 6 && (
                      <label className="aspect-square border-2 border-dashed border-stone-300 rounded flex items-center justify-center cursor-pointer hover:border-[#1B4F72]">
                        <input type="file" accept="image/*" multiple className="hidden" onChange={handlePortfolioImageUpload} />
                        <Plus className="w-5 h-5 text-stone-400" />
                      </label>
                    )}
                  </div>
                </div>
              </div>
              <Button onClick={addPortfolioItem} className="bg-[#1B4F72] hover:bg-[#154360] gap-2">
                <Plus className="w-4 h-4" /> Proje Ekle
              </Button>
            </div>

            {/* Portfolio List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(profile.portfolio || []).map((item) => (
                <div key={item.id} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                  {item.images?.[0] && (
                    <img src={item.images[0]} alt="" className="w-full h-40 object-cover" />
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-stone-900">{item.title}</h4>
                        <p className="text-xs text-stone-400">{item.client} · {item.year}</p>
                      </div>
                      <Button onClick={() => deletePortfolioItem(item.id)} variant="ghost" size="icon" className="text-stone-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {item.category && <Badge variant="outline" className="mt-2 text-xs">{item.category}</Badge>}
                    {item.description && <p className="text-sm text-stone-600 mt-2 line-clamp-2">{item.description}</p>}
                  </div>
                </div>
              ))}
            </div>
            {(!profile.portfolio || profile.portfolio.length === 0) && (
              <div className="text-center py-12 text-stone-400">
                <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Henüz portfolyo projesi eklenmedi</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
